import { useEffect, useState, useCallback } from 'react';
import { supabase, ALLOWED_USER_ID } from './supabase';

export interface AuthState {
  loading:  boolean;
  session:  Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'];
  user:     { id: string; email: string | null } | null;
  isAdmin:  boolean;  // verified server-side via is_admin() RPC
  allowed:  boolean;  // passes both user-id gate AND is_admin()
  error:    string | null;
}

/**
 * Hook that resolves the current auth state and verifies the user
 * is the hardcoded allowed id AND passes the server-side is_admin()
 * check. Either failure → allowed = false and the AuthGate redirects.
 */
export function useAuth(): AuthState & {
  signIn:  (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
} {
  const [state, setState] = useState<AuthState>({
    loading: true,
    session: null,
    user: null,
    isAdmin: false,
    allowed: false,
    error: null,
  });

  const verifyAndSet = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      setState({ loading: false, session: null, user: null, isAdmin: false, allowed: false, error: null });
      return;
    }

    // Belt: client-side user_id check.
    const passesIdGate = session.user.id === ALLOWED_USER_ID;

    // Braces: server-side is_admin() RPC. Double layer so if the env var
    // is tampered with locally, the server still says no.
    let isAdmin = false;
    if (passesIdGate) {
      const { data: adminRes } = await supabase.rpc('is_admin');
      isAdmin = adminRes === true;
    }

    setState({
      loading: false,
      session,
      user:    { id: session.user.id, email: session.user.email ?? null },
      isAdmin,
      allowed: passesIdGate && isAdmin,
      error:   null,
    });
  }, []);

  useEffect(() => {
    verifyAndSet();
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      verifyAndSet();
    });
    return () => sub.subscription.unsubscribe();
  }, [verifyAndSet]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    await verifyAndSet();
    return { error: null };
  }, [verifyAndSet]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setState({ loading: false, session: null, user: null, isAdmin: false, allowed: false, error: null });
  }, []);

  return { ...state, signIn, signOut };
}
